import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  styled
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { t } from '../../utils/textUtils';

// Estilos personalizados
const LanguageCard = styled(Card)(({ theme, selected }) => ({
  transition: 'transform 0.2s, box-shadow 0.2s',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
  }
}));

const SelectedBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  color: theme.palette.primary.main,
  backgroundColor: 'white',
  borderRadius: '50%',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
}));

const languages = [
  { code: 'id', name: 'Bahasa Indonesia', nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'ms', name: 'Bahasa Melayu', nativeName: 'Bahasa Melayu', flag: '🇲🇾' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' }
];

const LanguageSettings = () => {
  const { user } = useAuth();
  const [language, setLanguage] = useState('id'); // Default to Indonesian
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  
  useEffect(() => {
    const fetchLanguageSettings = async () => {
      if (!user) return;
      
      // Ensure user has an id property
      const userId = user.id || user.userId;
      if (!userId) {
        console.error('User ID is undefined');
        setAlert({
          open: true,
          message: 'ID pengguna tidak valid',
          severity: 'error'
        });
        setLoading(false);
        return;
      }
      
      console.log('Fetching language settings with user ID:', userId);
      
      setLoading(true);
      try {
        // Coba ambil pengaturan bahasa dari backend
        const response = await api.get(`/users/${userId}/language-settings`).catch((error) => {
          console.log('Language settings endpoint error:', error?.response?.status);
          // Jika endpoint tidak ada atau error, gunakan nilai dari localStorage
          const savedLanguage = localStorage.getItem('language');
          console.log('Using language from localStorage:', savedLanguage || 'id');
          return { data: { language: savedLanguage || 'id' } };
        });
        
        console.log('Language settings response:', response.data);
        setLanguage(response.data.language);
      } catch (error) {
        console.error('Error fetching language settings:', error);
        // Gunakan bahasa default jika terjadi error
        const savedLanguage = localStorage.getItem('language');
        setLanguage(savedLanguage || 'id');
      } finally {
        setLoading(false);
      }
    };
    
    fetchLanguageSettings();
  }, [user]);

  const handleLanguageSelect = (code) => {
    setLanguage(code);
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Simpan di localStorage untuk penggunaan langsung
      localStorage.setItem('language', language);
      
      // Coba kirim ke backend jika memungkinkan
      if (user?.id) {
        try {
          await api.put(`/users/${user.id}/language-settings`, { language })
            .catch(err => {
              console.log('Backend API not available, using localStorage only');
              // Tidak perlu menampilkan error ke user karena localStorage sudah berhasil
            });
        } catch (backendError) {
          console.log('Using localStorage only for language settings');
          // Tidak perlu menampilkan error ke user
        }
      }
      
      setAlert({
        open: true,
        message: 'Pengaturan bahasa berhasil disimpan',
        severity: 'success'
      });
      
      // Tampilkan pesan bahwa perubahan akan terlihat setelah refresh
      setTimeout(() => {
        setAlert({
          open: true,
          message: 'Refresh halaman untuk melihat perubahan',
          severity: 'info'
        });
      }, 2000);
      
    } catch (error) {
      console.error('Error saving language settings:', error);
      setAlert({
        open: true,
        message: 'Gagal menyimpan pengaturan bahasa',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <TranslateIcon sx={{ mr: 1 }} /> {t('PENGATURAN BAHASA')}
        </Typography>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? t('MENYIMPAN...') : t('SIMPAN PENGATURAN')}
        </Button>
      </Box>
      
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          {t('PILIH BAHASA')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('PILIH BAHASA YANG INGIN ANDA GUNAKAN DI APLIKASI')}
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={2}>
          {languages.map((lang) => (
            <Grid item xs={12} sm={6} md={4} key={lang.code}>
              <LanguageCard selected={language === lang.code}>
                <CardActionArea onClick={() => handleLanguageSelect(lang.code)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h4" sx={{ mr: 2 }}>
                        {lang.flag}
                      </Typography>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {lang.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lang.nativeName}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
                {language === lang.code && (
                  <SelectedBadge>
                    <CheckCircleIcon color="primary" />
                  </SelectedBadge>
                )}
              </LanguageCard>
            </Grid>
          ))}
        </Grid>
        
        <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(240, 193, 75, 0.08)', borderRadius: 1 }}>
          <Typography variant="body2">
            {t('PERUBAHAN BAHASA AKAN DITERAPKAN SETELAH ANDA MENYIMPAN PENGATURAN DAN MEMUAT ULANG APLIKASI.')}
          </Typography>
        </Box>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          {t('FORMAT TANGGAL DAN WAKTU')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('PILIH FORMAT TANGGAL DAN WAKTU YANG INGIN DIGUNAKAN')}
        </Typography>
        
        <RadioGroup
          aria-label="dateFormat"
          name="dateFormat"
          value="dd-mm-yyyy"
        >
          <FormControlLabel value="dd-mm-yyyy" control={<Radio />} label={t('DD-MM-YYYY (31-12-2023)')} />
          <FormControlLabel value="mm-dd-yyyy" control={<Radio />} label={t('MM-DD-YYYY (12-31-2023)')} />
          <FormControlLabel value="yyyy-mm-dd" control={<Radio />} label={t('YYYY-MM-DD (2023-12-31)')} />
        </RadioGroup>
      </Paper>
      
      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LanguageSettings;
